import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';

import { apiConnector } from '@/api/api.connector';
import { sessionUserFromToken } from '@/api/auth.api';
import { openAuth } from '@/pages/Auth';
import {
  isReturningUser,
  markReturningUser
} from '@/shared/services/auth-presence';
import { getUserToken } from '@/shared/services/user-hash';
import MainLoader from '@/shared/ui/main-loader';
import { useAppDispatch } from '@/store';
import { setAuthStatus, setUser } from '@/store/auth.slice';

/**
 * Определяет статус сессии на входе в приложение (паритет с нативным restore + unlock):
 * 1. есть refresh-токен → тихо пробуем refresh → `authenticated` (без UI);
 * 2. refresh не удался ИЛИ токена нет, но есть маркер вернувшегося → `returning`: открываем
 *    логин (пользователь авторизовался ранее — должен залогиниться снова);
 * 3. ни токена, ни маркера → `guest`: ходит по платформе свободно до закрытого ресурса.
 *
 * Гость и вернувшийся видят контент; вернувшемуся поверх открывается оверлей логина.
 */
const AuthorizedProvider: React.FC = () => {
  const dispatch = useAppDispatch();
  const [ready, setReady] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    // Защита от двойного запуска (StrictMode dev): bootstrap делаем один раз.
    if (started.current) return;
    started.current = true;

    void (async () => {
      const { refreshToken } = getUserToken();

      if (refreshToken) {
        try {
          await apiConnector.refresh();
          markReturningUser();
          // Телефон/идентификатор — из обновлённого access-токена (как нативный session-sync).
          dispatch(setUser(sessionUserFromToken()));
          dispatch(setAuthStatus('authenticated'));
          setReady(true);
          return;
        } catch {
          // refresh-токен протух/отозван — падаем в ветку вернувшегося/гостя ниже.
        }
      }

      if (isReturningUser()) {
        dispatch(setAuthStatus('returning'));
        // На лендинге (корень /:lang) оверлей логина не навязываем — это маркетинговая
        // страница; вернувшийся залогинится лениво при входе в закрытый ресурс.
        const isLanding = /^\/[a-z]{2}\/?$/i.test(window.location.pathname);
        // На страницах входа и восстановления пароля оверлей перекрыл бы саму форму.
        const isAuthPage = /^\/[a-z]{2}\/auth\//i.test(
          window.location.pathname
        );
        if (!isLanding && !isAuthPage) void openAuth('login');
      } else {
        dispatch(setAuthStatus('guest'));
      }
      setReady(true);
    })();
  }, [dispatch]);

  if (!ready) return <MainLoader fullScreen />;

  return <Outlet />;
};

export default AuthorizedProvider;
